// src/ride/RideRequestsHub.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import RideBottomNav from "../components/RideBottomNav";
import { MessageCircle } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE;

// 💰 Calculate final ride cost (with stops, per seat)
const calculateFinalPrice = (req) => {
  let base = req.pricePerSeat || 0;

  // Detect stop-to-stop price difference
  if (req.bookedFrom && req.bookedTo && req.stops?.length > 0) {
    const fromStop = req.stops.find((s) => s.name === req.bookedFrom);
    const toStop = req.stops.find((s) => s.name === req.bookedTo);

    if (fromStop && toStop) {
      base = toStop.price - fromStop.price;
    }
  }

  return base * (req.seats || 1);
};

const RideRequestsHub = () => {
  const navigate = useNavigate();
  const workerId = localStorage.getItem("workerId");

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workerId) return;

    const fetchRequests = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/ride/worker/${workerId}/requests`
        );

        console.log("Hub Data:", res.data);

        const sorted = res.data.hub.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setRequests(sorted);
      } catch (err) {
        console.error("RideRequestsHub error:", err);
      }

      setLoading(false);
    };

    fetchRequests();
  }, [workerId]);

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500">
        Loading ride requests...
      </div>
    );
  }

  return (
    <div className="pb-20 px-4 font-[Poppins]">

      {/* Title */}
      <h1 className="text-3xl font-extrabold text-center my-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600 drop-shadow">
        Ride Requests
      </h1>

      {/* No requests */}
      {requests.length === 0 && (
        <p className="text-center text-gray-500 mt-8">No requests yet.</p>
      )}

      {/* List */}
      <div className="space-y-6">
        {requests.map((req) => {
          const price = calculateFinalPrice(req);

          const customer = req.customer || {};
          const photo = customer.profilePhotoUrl;
          const letter = (customer.name || "P").charAt(0).toUpperCase();

          return (
            <div
              key={req.requestId}
              onClick={() => navigate(`/ride/requests/${req.rideId}`)}
              className="relative p-5 bg-white border shadow-lg rounded-2xl hover:shadow-2xl transition cursor-pointer"
            >
              {/* Price */}
              <div className="absolute top-4 right-4 bg-blue-600 text-white px-4 py-1 rounded-full shadow text-sm font-semibold">
                ${price}
              </div>

              {/* Passenger info */}
              <div className="flex items-center gap-4">
                {/* Avatar */}
                {photo ? (
                  <img
                    src={photo}
                    alt={customer.name}
                    className="w-12 h-12 rounded-full object-cover shadow"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg shadow">
                    {letter}
                  </div>
                )}

                <div>
                  <p className="font-semibold text-lg">{customer.name || "Passenger"}</p>
                  <p className="text-gray-500 text-sm italic">
                    {req.message || "New request"}
                  </p>
                </div>
              </div>

              {/* Ride details */}
              <div className="mt-4 text-sm text-gray-700 leading-relaxed">
                <p>
                  <span className="font-semibold">Route:</span>{" "}
                  {req.from} → {req.to}
                </p>
                <p className="mt-1">
                  <span className="font-semibold">When:</span>{" "}
                  {req.date} at {req.time}
                </p>
                {req.bookedFrom && req.bookedTo && (
                  <p className="mt-1 text-indigo-600">
                    <span className="font-semibold">Stop:</span>{" "}
                    {req.bookedFrom} → {req.bookedTo}
                  </p>
                )}
              </div>

              {/* Chat button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/worker/ride-chat/${req.rideId}/${customer._id}`);
                }}
                className="absolute bottom-4 right-4 bg-white p-2 rounded-full shadow hover:bg-blue-50 transition"
              >
                <MessageCircle className="w-5 h-5 text-blue-600" />
              </button>
            </div>
          );
        })}
      </div>

      <RideBottomNav />
    </div>
  );
};

export default RideRequestsHub;
